/*
  global
  DEBUG,
  SENTRY_CLIENT_DSN,
  SENTRY_ENVIRONMENT,
  SENTRY_RELEASE
*/
import "./global.css";
import { logTiming } from "@services/analytics";
import isHlsExperience from "@utils/is-hls-experience";
import loadHls from "@utils/load-hls";
import {
  iOSSafariStandalone,
  supportsEnhancedExperience,
  supportsNativeHls
} from "@utils/support";
import App from "./App.svelte";

/**
 * Initialise speakeasy client application
 * Loads Hls.js if necessary
 * Loads Sentry if production
 */
// eslint-disable-next-line max-lines-per-function
export default async function bootstrapSpeakeasyClient() {
  if (!DEBUG) {
    const [Sentry, { Integrations }] = await Promise.all([
      import(
        /* webpackChunkName: "sentry-browser" */
        "@sentry/browser"
      ),
      import(
        /* webpackChunkName: "sentry-tracing" */
        "@sentry/tracing"
      )
    ]);
    let rangeErrorSent = false;

    Sentry.init({
      beforeSend(event, hint) {
        const err = hint.originalException;
        if (err && err instanceof RangeError) {
          if (rangeErrorSent) {
            return null;
          }
          rangeErrorSent = true;
        }
        return event;
      },
      dsn: SENTRY_CLIENT_DSN,
      environment: SENTRY_ENVIRONMENT,
      integrations: [new Integrations.BrowserTracing()],
      release: SENTRY_RELEASE,
      sampleRate: 0.2
    });
  }

  // eslint-disable-next-line no-underscore-dangle
  const data = window.__SPEAKEASY_INITIAL_DATA__ || {};
  const experience = data.experience || {};
  let canCreateClient = false;

  if (isHlsExperience(experience) && !supportsNativeHls) {
    try {
      canCreateClient = await loadHls();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  } else {
    canCreateClient = true;
  }

  if (canCreateClient) {
    const speakeasy = new App({
      target: document.querySelector("#speakeasyapp"),
      hydrate: true,
      props: data
    });

    document.documentElement.classList.add("enhanced");

    // eslint-disable-next-line require-atomic-updates
    window.speakeasy = speakeasy;

    if (window.performance) {
      logTiming(
        "client",
        "load",
        Math.round(performance.now()),
        "bootstrap convo client"
      );
    }
  }
}

window.addEventListener("DOMContentLoaded", () => {
  if (supportsEnhancedExperience) {
    bootstrapSpeakeasyClient();
  }

  document.documentElement.classList.remove("preloading");

  if (iOSSafariStandalone) {
    // eslint-disable-next-line no-alert
    alert("To get the full experience, open this page in your browser");
  }
});
